import { useEffect, useRef, useState } from 'react';
import { verifyReservation } from '../../openapi/reservations/reservations';
import { useCart } from './useCart';

const DEBOUNCE_MS = 400;

export type VerificationStatus = 'idle' | 'loading' | 'ok' | 'error';

export interface IVerificationState {
    status: VerificationStatus;
    deltaCents: number;
    errorCode: number | string | null;
    errorMessage: string | null;
}

const IDLE_STATE: IVerificationState = {
    status: 'idle',
    deltaCents: 0,
    errorCode: null,
    errorMessage: null,
};

/**
 * Debounced preflight against the backend's verifyReservation endpoint.
 *
 * Runs only when the show has a verification script configured
 * (`hasVerificationScript`). Re-fires whenever the cart, seats, timeslot,
 * coupon, contact email or language change. The result is advisory — the
 * backend re-runs the same script at addReservation as the authoritative gate.
 */
export function useVerificationPreflight({
    showId,
    timeslotId,
    hasVerificationScript,
    language,
}: {
    showId: number | undefined;
    timeslotId: number | undefined;
    hasVerificationScript: boolean;
    language: string;
}): IVerificationState {
    const { cart, seats, appliedCoupon, genericBookingFields, setVerificationDeltaCents } =
        useCart();
    const [state, setState] = useState<IVerificationState>(IDLE_STATE);
    const requestSeqRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (!hasVerificationScript || !showId || !timeslotId || cart.length === 0) {
            setState(IDLE_STATE);
            setVerificationDeltaCents(0);
            return undefined;
        }

        setState((prev) => (prev.status === 'loading' ? prev : { ...prev, status: 'loading' }));

        const seq = ++requestSeqRef.current;
        timerRef.current = setTimeout(() => {
            verifyReservation({
                showId,
                timeslotId,
                cart: cart.map((c) => ({
                    ticketTypeId: c.ticketTypeId,
                    ticketOptionId: c.ticketOptionId,
                    quantity: c.quantity,
                })),
                seats: seats.map((s) => ({ id: s.id, ticketTypeId: s.ticketTypeId })),
                couponCode: appliedCoupon?.code,
                genericBookingFields,
            } as any)
                .then((res: any) => {
                    if (seq !== requestSeqRef.current) return;
                    if (res?.ok) {
                        const delta = res.deltaCents || 0;
                        setState({
                            status: 'ok',
                            deltaCents: delta,
                            errorCode: null,
                            errorMessage: null,
                        });
                        setVerificationDeltaCents(delta);
                    } else {
                        setState({
                            status: 'error',
                            deltaCents: 0,
                            errorCode: res?.error?.code ?? null,
                            errorMessage: res?.error?.message ?? null,
                        });
                        setVerificationDeltaCents(0);
                    }
                })
                .catch((err: any) => {
                    if (seq !== requestSeqRef.current) return;
                    setState({
                        status: 'error',
                        deltaCents: 0,
                        errorCode: null,
                        errorMessage:
                            err?.response?.data?.message ||
                            (err instanceof Error ? err.message : null),
                    });
                    setVerificationDeltaCents(0);
                });
        }, DEBOUNCE_MS);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showId,
        timeslotId,
        hasVerificationScript,
        language,
        cart,
        seats,
        appliedCoupon?.code,
        genericBookingFields.email,
        setVerificationDeltaCents,
    ]);

    return state;
}
